/* globals ifCl, license */
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import { retrieveRange } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import DownSampling from "common/graphs/DownSampling";
import {
  PERIOD_TO_HOURS,
  PERIOD_TO_LINES,
  altPeriodScopeChoices,
  HOURS_TO_PERIOD,
} from "common/constants";
import Chart from "./Chart";
import { getRowsBqn } from "../Protocols/api";

import { btaStatsRetrieval, licenseRetrieval, parseStatsBtaData } from "./api";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const ChartContainer = styled.div`
  min-height: 500px;
  margin-top: 20px;
  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }
  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }
  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }
  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

const defaultContext = "Day";

const ULTP = 1.25;

const btaChartMargin = { top: 20, right: 40, bottom: 30, left: 60 };

const BtaChartWrapped = ({ limits, isZeroOrNull, threshold, ...props }) => {
  const max = limits?.[1] ?? 0;
  const yMax =
    !isZeroOrNull &&
    threshold &&
    threshold < max * ULTP &&
    threshold > max * 0.9
      ? max * ULTP
      : max;
  const hasLimit = !isZeroOrNull && threshold && threshold <= yMax * ULTP;
  return (
    <Chart
      {...props}
      hasLimit={hasLimit}
      threshold={threshold}
      fixedYRange={isZeroOrNull ? [0, 1] : threshold ? [0, yMax] : [0, null]}
    />
  );
};

const BtaChartDownsampled = (props) => (
  <DownSampling
    Chart={BtaChartWrapped}
    margin={btaChartMargin}
    requiresZeroOrNull={true}
    {...props}
  />
);

const getThreshold = (license, maxUsage) => {
  if (!license || maxUsage === "n/a") {
    return undefined;
  }
  return Number(maxUsage);
};

const retrieveBta = ({ context, from, to, mode, bqnId }) =>
  btaStatsRetrieval({ lines: PERIOD_TO_LINES[context], mode, bqnId }).then(
    (response) => parseStatsBtaData(response, from, to),
  );

const retrieveLicense = (mode, bqnId) =>
  licenseRetrieval(mode, bqnId).then((response) => {
    const [header, ...rows] = response.trim().split("\n");
    const headerAttrs = header.trim().split(/\s+/);
    const headerIndexMap = headerAttrs.reduce((acc, value, index) => {
      acc[value] = index;
      return acc;
    }, {});

    const rowsBqn = getRowsBqn(rows, mode, bqnId, headerIndexMap);

    if (rowsBqn.length === 0) {
      return null;
    }

    const attrs = rowsBqn[0].trim().split(/\s+/);
    const feature = attrs[headerIndexMap["FEATURE"]];
    const maxUsage = attrs[headerIndexMap["USAGE-MAX"]];
    const lic =
      feature === "bta"
        ? "bta"
        : feature === "bta-base"
          ? "bta-base"
          : "bta-core";

    const threshold = getThreshold(lic, maxUsage);
    return threshold;
  });

const formatInt = d3.format(".0f");
const formatOneDec = d3.format(".1f");
const formatTwoDec = d3.format(".2f");

const variablePrecisionFormat = (d) =>
  d === 0 || d >= 10.0
    ? formatInt(d)
    : d >= 1.0
      ? formatOneDec(d)
      : formatTwoDec(d);

const setAndCopy = (setter) => (value) => {
  setter(value);
  return value;
};

export const useStatsBtaTrafficData = ({
  context = defaultContext,
  firstRequest = null,
  mode,
  bqnId,
}) => {
  const [request, setRequest] = useState(firstRequest);
  const [initialRange, setInitialRange] = useState();
  const doRequest = (context) =>
    retrieveRange(PERIOD_TO_HOURS[context], mode, bqnId)
      .then(setAndCopy(setInitialRange))
      .then(({ from, to }) =>
        Promise.all([
          retrieveBta({ context, from, to, mode, bqnId }),
          retrieveLicense(mode, bqnId),
        ]),
      )
      .then(([responseBta, responseLicense]) => ({
        ...responseBta,
        threshold: responseLicense,
      }));

  const doLoad = () => {
    setRequest(doRequest(context));
  };

  return { request, doLoad, initialRange };
};

const BtaTrafficReport = ({ returnView = null }) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const actions = useContext(ActionsContext);
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [period, setPeriod] = useState(
    HOURS_TO_PERIOD[hoursOfReturnView] ?? "Day",
  );
  const { request, doLoad, initialRange } = useStatsBtaTrafficData({
    context: period,
    firstRequest: null,
    bqnId,
    mode,
  });
  const [range, setRange] = useState();

  const doRestart = () => {
    setRange(undefined);
    doLoad();
  };
  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doRestart();
    return actions.recv("do-load", doRestart);
  }, [period, bqnId, mode]);

  return (
    <Wrapper>
      <ArrangeInColumns rowGap="0">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={period}
            onChange={({ target }) => setPeriod(target.value)}
            options={altPeriodScopeChoices}
          />
        </SelectWrapper>
      </ArrangeInColumns>
      <ChartContainer>
        <Request during={request}>
          {({ items: initItems, fields, threshold }) => {
            const selectedRange = range || initialRange;
            return (
              <BtaChartDownsampled
                items={initItems}
                fields={fields.map((field) => ({ name: field, label: field }))}
                range={selectedRange}
                threshold={threshold}
                yAxisFormat={variablePrecisionFormat}
                xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                yAxisTopGap={0.05}
                yAxisTicksNumber={5}
                xAxisTicksNumber={8}
                yTooltipFormat={(d) => d3.format(".2f")(d) + " Mbps"}
              />
            );
          }}
        </Request>
      </ChartContainer>
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default BtaTrafficReport;
